import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Provider, useAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useMountEffect } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FundDocument } from '~/api/def/endpoints/fundEndpoint';
import {
  documentTableLoading,
  fundIdAtom,
  portfolioIdAtom,
} from '~/atoms/document/documentAtoms';
import { DocumentOverviewStore } from '~/atoms/document/documentOverviewAtoms';
import { DialogDocumentOverview } from '~/components/ui/document/dialogs/documentOverviewDialog/DialogDocumentOverview';
import { DocumentCompareContainer } from '~/components/ui/document/DocumentCompareContainer';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import { getColDefsDocumentTable } from '~/components/ui/tables/ag/documentsTable/colDefsDocumentTable';
import { DocumentFilterPanel } from '~/components/ui/tables/ag/documentsTable/DocumentFilterPanel';
import { getDocumentsContextMenu } from '~/components/ui/tables/ag/documentsTable/getDocumentsContextMenu';
import { useDocumentLoader } from '~/components/ui/tables/ag/documentsTable/useDocumentLoader';
import { useDocumentsUrlParamsPortfolio } from '~/components/ui/tables/ag/documentsTable/useDocumentsUrlParamsPortfolio';
import { DocumentUploaderQueue } from '~/components/ui/uploaders/DocumentUploaderQueue';
import { GoogleDriveUploader } from '~/components/ui/uploaders/GoogleDriveUploader';
import { useFilterTable } from '~/lib/table/useFilterTable';

type DocumentsSectionProps = {
  fundId: string;
  portfolioCompanyId: string;
};

export default function DocumentsSection({
  fundId,
  portfolioCompanyId,
}: DocumentsSectionProps) {
  const [loadingDocuments] = useAtom(documentTableLoading);
  const [colDefs, setColDefs] = useState<ColDef<FundDocument>[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<FundDocument['type'][]>(
    [],
  );
  const { loadDocuments, documents } = useDocumentLoader();
  const gridRef = useRef<AgGridReact<FundDocument> | null>(null);
  const {
    filteredData,
    setExternalFilters,
    externalFilters,
    setFundNameSearch,
    doesExternalFilterPass,
    fundNameSearch,
  } = useFilterTable(documents ?? []);
  const [isUploadDialogVisible, setIsUploadDialogVisible] =
    useState<boolean>(false);

  const {
    isDocumentCompareDialogVisible,
    showDocumentOverview,
    hideDocumentCompare,
    showDocumentsCompare,
  } = useDocumentsUrlParamsPortfolio();
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setAtomFundId] = useAtom(fundIdAtom);
  const [, setPortfolioIdAtom] = useAtom(portfolioIdAtom);

  /** Set default state of the status filters
   * */
  useMountEffect(() => {
    setExternalFilters({ status: 'Live' });
  });

  /** Collect all unique agreement types from the documents
   *  TODO remove this logic fom component body
   * */
  useEffect(() => {
    if (documents) {
      const agreementTypes = documents.reduce(
        (acc: FundDocument['type'][], doc) => {
          if (!acc.includes(doc.type)) {
            acc.push(doc.type);
          }
          return acc;
        },
        [],
      );
      setAgreementTypes(agreementTypes);
    }
  }, [documents]);

  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState<FundDocument[]>(
    [],
  );

  useEffect(() => {
    if (fundId) {
      setAtomFundId(fundId);
    }
    return () => {
      setAtomFundId(null);
    };
  }, [fundId, setAtomFundId]);

  useEffect(() => {
    if (portfolioCompanyId) {
      setPortfolioIdAtom(portfolioCompanyId);
    }
    return () => {
      setPortfolioIdAtom(null);
    };
  }, [portfolioCompanyId, setPortfolioIdAtom]);

  useEffect(() => {
    setColDefs(getColDefsDocumentTable({ showDocumentOverview }));
  }, [showDocumentOverview]);

  const ctxDocumentsContextMenu = (
    params: GetContextMenuItemsParams<FundDocument>,
  ): (string | MenuItemDef)[] => {
    return getDocumentsContextMenu(params, {
      showDocumentOverview,
      loadDocuments,
    });
  };

  /** trigger table filter when external filter on fundName changes
   * !IMPORTANT: api.onFilterChanged() has to be called to trigger the filter
   * */
  const externalFilterChanged = useCallback(
    (newValue: string) => {
      setFundNameSearch(newValue);
      gridRef.current?.api.onFilterChanged();
    },
    [setFundNameSearch],
  );

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes() || [];
    setSelectedDocuments(
      selectedNodes
        .map((node) => node.data)
        .filter((doc): doc is FundDocument => doc !== undefined),
    );
    setSelectedCount(selectedNodes.length);
  }, []);

  const isRowSelectable = useCallback(
    (node: { isSelected: () => boolean }) => {
      return selectedCount < 2 || node.isSelected();
    },
    [selectedCount],
  );

  return (
    <div
      className="flex flex-column mt-3 w-full p-3 gray-border"
      style={{
        borderRadius: '8px',
        height: 'calc(100vh - 15rem)',
      }}
    >
      <>
        <Provider store={DocumentOverviewStore}>
          <DialogDocumentOverview
            portfolioCompanyId={portfolioCompanyId}
            source="portfolioDocuments"
          />
        </Provider>

        <Dialog
          onHide={() => {
            setSelectedDocuments([]);
            hideDocumentCompare();
          }}
          header="Document Compare"
          style={{ width: '100vw', height: '100vh', maxHeight: '100vh' }}
          visible={isDocumentCompareDialogVisible}
          pt={{ content: { style: { height: '100%' } } }}
        >
          <DocumentCompareContainer
            visible={isDocumentCompareDialogVisible}
            fundName={''}
            userSelectedDocuments={selectedDocuments}
          />
        </Dialog>

        <Dialog
          onHide={() => setIsUploadDialogVisible(false)}
          header="Upload Document"
          style={{ width: '80vw', minHeight: '36rem' }}
          visible={isUploadDialogVisible}
        >
          <div className="p-m-5">
            <TabView
              activeIndex={activeIndex}
              onTabChange={(e) => setActiveIndex(e.index)}
            >
              <TabPanel header="Local Machine">
                <DocumentUploaderQueue
                  fundId={fundId || ''}
                  portfolioCompanyId={portfolioCompanyId}
                  onSubmitComplete={() => loadDocuments(fundId)}
                />
              </TabPanel>
              <TabPanel header="Google Drive">
                <GoogleDriveUploader
                  onSubmitComplete={() => loadDocuments(fundId)}
                  onUploadInit={() => setIsUploadDialogVisible(false)}
                  fundId={fundId || ''}
                  portfolioCompanyId={portfolioCompanyId}
                />
              </TabPanel>
            </TabView>
          </div>
        </Dialog>

        <DocumentFilterPanel
          agreementTypes={agreementTypes}
          filters={externalFilters}
          setFilter={setExternalFilters}
        />
        <div className="document_search border-gray-300 pt-2 mt-2 pb-2 flex align-items-center justify-content-between border-top-1">
          <div className="flex align-items-center">
            <h3 className="mb-0">Fundraising Documents</h3>

            <InputText
              value={fundNameSearch}
              className="search-input ml-3"
              style={{
                backgroundColor: 'var(--bq-color-gray-lighter)',
              }}
              onChange={(e) => externalFilterChanged(e.target.value)}
              placeholder="Search by Document Name"
            />
          </div>
          <div>
            <Button
              label="Upload "
              onClick={() => setIsUploadDialogVisible(true)}
              icon="pi pi-plus"
            />
            <Button
              label="Compare document"
              disabled={selectedDocuments.length < 2}
              onClick={async () => showDocumentsCompare()}
              icon="pi pi-align-left"
              className="ml-4"
            />
          </div>
        </div>
        <div
          className={'ag-theme-quartz'}
          style={{ flex: '1 1 0px', width: '100%', height: '75vh' }}
        >
          <AgGridReact<FundDocument>
            rowData={filteredData}
            ref={gridRef}
            loading={loadingDocuments}
            suppressCellFocus
            getRowId={(data) => data.data.id}
            isExternalFilterPresent={() => fundNameSearch !== ''}
            doesExternalFilterPass={doesExternalFilterPass}
            // getMainMenuItems={getMainMenuItems}
            onGridSizeChanged={() => gridRef.current?.api.sizeColumnsToFit()}
            getContextMenuItems={ctxDocumentsContextMenu}
            columnDefs={colDefs}
            columnTypes={colTypesWidths}
            tooltipShowDelay={0}
            theme={agTheme}
            autoSizeStrategy={{
              type: 'fitGridWidth',
              defaultMinWidth: 100,
            }}
            rowSelection={{
              mode: 'multiRow',
              isRowSelectable: isRowSelectable,
              headerCheckbox: false,
            }}
            onSelectionChanged={onSelectionChanged}
          />
        </div>
      </>
    </div>
  );
}
